/**
 * Single material row in Tab "Học liệu".
 * Click: file → fetch presigned URL → open in new tab; link → open url directly.
 */
import {
  File,
  Link2,
  Video,
  Music,
  FileText,
  Presentation,
  Loader2,
} from 'lucide-react';
import type { DashboardMaterial } from '@client/lib/queries/use-session-dashboard';

const TYPE_ICONS: Record<DashboardMaterial['type'], React.ElementType> = {
  file:     File,
  link:     Link2,
  video:    Video,
  audio:    Music,
  document: FileText,
  slide:    Presentation,
};

const TYPE_LABELS: Record<DashboardMaterial['type'], string> = {
  file:     'Tệp',
  link:     'Liên kết',
  video:    'Video',
  audio:    'Âm thanh',
  document: 'Tài liệu',
  slide:    'Slide',
};

interface MaterialRowProps {
  material: DashboardMaterial;
  onOpen: (material: DashboardMaterial) => void;
  isLoading?: boolean;
}

export function MaterialRow({ material, onOpen, isLoading }: MaterialRowProps) {
  const Icon = TYPE_ICONS[material.type] ?? File;

  return (
    <button
      type="button"
      onClick={() => onOpen(material)}
      disabled={isLoading}
      className="flex w-full items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-accent/50 disabled:opacity-60 min-h-[56px]"
    >
      <div className="mt-0.5 shrink-0 text-muted-foreground">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">{material.title}</p>
        {material.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{material.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">{TYPE_LABELS[material.type]}</p>
      </div>
    </button>
  );
}
